import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { FileText, Upload, Download, Trash2, Eye, X, Camera, Image as ImageIcon, RefreshCw, FileDown, File as FileIcon } from "lucide-react";
import InvoiceIdentity from "../components/InvoiceIdentity";
import UploadedInvoiceReview from "../components/UploadedInvoiceReview";
import ExportApprovalStatus, { handleExportError } from "../components/ExportApprovalStatus";

const OCR_LABEL = { pending: "جارٍ الاستخراج...", done: "تم الاستخراج", failed: "فشل الاستخراج" };
const OCR_COLOR = { pending: "bg-amber-100 text-amber-800", done: "bg-green-100 text-green-800", failed: "bg-red-100 text-red-800" };
const REVIEW_PILL = { needs_review: ["بحاجة لمراجعة", "bg-amber-100 text-amber-800"], auto_ok: ["متسقة", "bg-emerald-100 text-emerald-800"], confirmed: ["مؤكدة", "bg-[#EBF4F6] text-[#09637E]"] };

const TYPE_FILTERS = [["all", "الكل"], ["online", "مبيعات أونلاين"], ["physical", "بيع فيزيائي"], ["individual_online", "بيع الأونلاين الفردي"], ["return", "مرتجعات"], ["payment", "دفعات"]];
const TYPE_BADGE = { online: "bg-[#EBF4F6] text-[#09637E]", physical: "bg-amber-50 text-amber-800 border border-amber-200", individual_online: "bg-sky-50 text-sky-800 border border-sky-200", return: "bg-rose-50 text-rose-700 border border-rose-200", payment: "bg-emerald-50 text-emerald-800 border border-emerald-200" };

const PdfInvoicesList = ({ isAdmin, isEmployee = false }) => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    // Admin keeps the original online-invoices endpoint; merchants get the unified read-only view (online + physical)
    api.get(isAdmin ? "/admin/invoices" : "/invoices/all")
      .then((r) => {
        if (isAdmin) { setRows(r.data.map((inv) => ({ ...inv, invoice_type: "online", invoice_type_ar: "بيع أونلاين", total: inv.final_total, pdf_url: `/invoices/${inv.id}/pdf`, counterpart: `البائع: ${inv.seller?.store_name} ← المشتري: ${inv.buyer?.store_name}` }))); }
        else { setRows(r.data.items); setCounts(r.data.counts || {}); }
      }).catch((e) => toast.error(arabicError(e)))
      .finally(() => setLoading(false));
  }, [isAdmin]);
  const visible = filter === "all" ? rows : rows.filter((r) => r.invoice_type === filter);

  const exportSales = async () => {
    setExporting(true);
    try {
      const r = await api.get("/export/sales", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `imdad_sales_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("تم تنزيل ملف Excel");
    } catch (e) { await handleExportError(e); }
    finally { setExporting(false); }
  };

  const download = async (inv) => {
    try {
      const r = await api.get(inv.pdf_url, { responseType: "blob" });   // source-specific endpoint (online vs physical)
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(arabicError(e)); }
  };

  const salesBtn = !isAdmin && !isEmployee && (
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      <button onClick={exportSales} disabled={exporting} data-testid="export-sales-btn"
        className="h-11 px-4 rounded-lg border-2 border-[#09637E] text-[#09637E] text-sm font-bold flex items-center gap-2 hover:bg-[#EBF4F6] disabled:opacity-60">
        <FileDown size={16} /> {exporting ? "جارٍ التصدير..." : "بيانات البيع من الموقع (Excel)"}
      </button>
      <ExportApprovalStatus />
    </div>
  );
  const filters = !isAdmin && (
    <div className="flex flex-wrap gap-2" data-testid="invoice-type-filters">
      {TYPE_FILTERS.map(([k, label]) => (
        <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`invoice-filter-${k}`}
          className={`h-10 px-4 rounded-full text-sm font-bold ${filter === k ? "bg-[#09637E] text-white" : "bg-[#EBF4F6] text-[#09637E] hover:bg-[#DCEBEE]"}`}>
          {label}{k !== "all" && counts[k] != null ? ` (${counts[k]})` : k === "all" && rows.length ? ` (${rows.length})` : ""}
        </button>
      ))}
    </div>
  );

  if (loading) return <div className="text-center text-[#666666] py-8">جارٍ التحميل...</div>;
  if (!rows.length) return (
    <div className="space-y-3">
      {salesBtn}
      <div className="bg-white border border-[#7AB2B2] rounded-2xl p-10 text-center" data-testid="empty-pdf-invoices">
        <FileText className="mx-auto text-[#7AB2B2] mb-3" size={44} />
        <p className="text-[#666666] font-bold">لا توجد فواتير بعد — تصدر الفاتورة تلقائياً عند اكتمال كل عملية بيع</p>
      </div>
    </div>
  );
  return (
    <div className="space-y-3">
      {salesBtn}
      {filters}
    <div className="space-y-2" data-testid="pdf-invoices-list">
      {!visible.length && <div className="bg-white border border-[#7AB2B2] rounded-xl p-6 text-center text-[#666666] text-sm" data-testid="empty-filtered-invoices">لا توجد فواتير من هذا النوع</div>}
      {visible.map((inv) => (
        <div key={inv.id} className="bg-white border border-[#7AB2B2] rounded-xl p-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`invoice-${inv.id}`} data-invoice-type={inv.invoice_type}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-extrabold text-[#09637E] text-sm" dir="ltr">{inv.invoice_number}</span>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${TYPE_BADGE[inv.invoice_type] || TYPE_BADGE.online}`} data-testid={`invoice-type-${inv.id}`}>{inv.invoice_type_ar}</span>
            </div>
            <div className="text-xs text-[#666666] break-words">
              {inv.date}
              {(inv.invoice_type === "physical" || inv.invoice_type === "individual_online") ? <> · الزبون: {inv.counterpart || "—"}{inv.seller_name ? <> · باع: {inv.seller_name}</> : null}</>
                : inv.invoice_type === "return" ? <> · الزبون: {inv.counterpart || "—"}{inv.original_invoice_number ? <> · الفاتورة الأصلية: <span dir="ltr">{inv.original_invoice_number}</span></> : null}{inv.seller_name ? <> · سجّله: {inv.seller_name}</> : null}</>
                : inv.invoice_type === "payment" ? <> · الزبون: {inv.counterpart || "—"}{inv.original_invoice_number ? <> · الفاتورة الأصلية: <span dir="ltr">{inv.original_invoice_number}</span></> : null}{inv.seller_name ? <> · سجّلها: {inv.seller_name}</> : null}</>
                : <> · {isAdmin ? inv.counterpart : <>الطرف الآخر: {inv.counterpart}</>}</>}
              {inv.direction && <span className="mr-2 font-bold text-[#333333]">({inv.direction})</span>}
            </div>
            {(inv.invoice_type === "physical" || inv.invoice_type === "individual_online" || inv.invoice_type === "payment") && inv.paid != null && (
              <div className="text-[11px] text-[#666666] mt-0.5 tabular-nums" data-testid={`invoice-ledger-${inv.id}`}>
                {inv.invoice_type === "payment" ? "إجمالي المدفوع" : "المدفوع"}: <span dir="ltr">${Number(inv.paid).toFixed(2)}</span> · المتبقي: <span dir="ltr" className={inv.remaining > 0 ? "text-amber-700 font-bold" : "text-green-700 font-bold"}>${Number(inv.remaining || 0).toFixed(2)}</span> · {inv.status}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-extrabold text-[#09637E] tabular-nums" dir="ltr">${Number(inv.total ?? inv.final_total ?? 0).toFixed(2)}</span>
            <button onClick={() => download(inv)} data-testid={`download-pdf-${inv.id}`}
              className="h-9 px-3 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white text-xs font-bold flex items-center gap-1">
              <Download size={14} /> PDF
            </button>
          </div>
        </div>
      ))}
    </div>
    </div>
  );
};

const UploadedInvoices = ({ isAdmin, isEmployee = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [uploading, setUploading] = useState(false);
  const camRef = useRef(null);
  const galRef = useRef(null);
  const pdfRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get(isAdmin ? "/admin/uploaded-invoices" : "/uploaded-invoices/mine")).data); }
    catch (e) { toast.error(arabicError(e)); }
    finally { setLoading(false); }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
    if (!isPdf && !f.type.startsWith("image/")) { toast.error("يُسمح بصورة أو ملف PDF فقط"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("حجم الملف كبير (الحد 8MB)"); return; }
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const rd = new FileReader(); rd.onload = () => res(rd.result); rd.onerror = rej; rd.readAsDataURL(f);
      });
      await api.post("/uploaded-invoices", { image_base64: b64, file_name: f.name });
      toast.success("تم رفع الفاتورة وجارٍ استخراج بياناتها تلقائياً");
      load();
      setTimeout(load, 12000);
    } catch (err) { toast.error(arabicError(err)); }
    finally { setUploading(false); }
  };

  const downloadOriginal = async (r) => {
    try {
      const res = await api.get(`/uploaded-invoices/${r.id}/file`, { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename\*=UTF-8''([^;]+)/);
      const name = m ? decodeURIComponent(m[1]) : (r.file_name || `invoice_${r.id.slice(0, 8)}`);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(arabicError(e)); }
  };

  const openView = async (r) => {
    try { setView((await api.get(`/uploaded-invoices/${r.id}`)).data); }
    catch (e) { toast.error(arabicError(e)); }
  };
  const del = async (r) => {
    if (!window.confirm("حذف هذه الفاتورة؟")) return;
    try { await api.delete(`/uploaded-invoices/${r.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(arabicError(e)); }
  };
  const exportExcel = async () => {
    try {
      const r = await api.get("/uploaded-invoices/export", { params: isAdmin ? { all: true } : {}, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "uploaded_invoices.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { await handleExportError(e); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        {!isAdmin && (
          <>
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="invoice-camera-input" />
            <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="invoice-gallery-input" />
            <input ref={pdfRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFile} data-testid="invoice-pdf-input" />
            <button onClick={() => camRef.current?.click()} disabled={uploading} data-testid="invoice-capture-btn"
              className="h-11 px-4 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60 md:hidden">
              <Camera size={16} /> التقاط صورة
            </button>
            <button onClick={() => galRef.current?.click()} disabled={uploading} data-testid="invoice-upload-btn"
              className="h-11 px-4 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60">
              <ImageIcon size={16} /> {uploading ? "جارٍ الرفع..." : "اختيار صورة من جهازك"}
            </button>
            <button onClick={() => pdfRef.current?.click()} disabled={uploading} data-testid="invoice-upload-pdf-btn"
              className="h-11 px-4 rounded-lg bg-[#088395] hover:bg-[#09637E] text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60">
              <FileIcon size={16} /> رفع فاتورة PDF
            </button>
          </>
        )}
        {!isEmployee && <button onClick={exportExcel} data-testid="export-uploaded-invoices"
          className="h-11 px-4 rounded-lg border-2 border-[#09637E] text-[#09637E] text-sm font-bold flex items-center gap-2 hover:bg-[#EBF4F6]">
          <Download size={16} /> تصدير الفواتير المرفوعة (Excel)
        </button>}
        <button onClick={load} className="h-11 w-11 rounded-lg border border-[#7AB2B2] text-[#09637E] flex items-center justify-center hover:bg-[#EBF4F6]" data-testid="refresh-uploaded">
          <RefreshCw size={16} />
        </button>
      </div>

      {loading ? <div className="text-center text-[#666666] py-8">جارٍ التحميل...</div> :
        !rows.length ? (
          <div className="bg-white border border-[#7AB2B2] rounded-2xl p-10 text-center" data-testid="empty-uploaded-invoices">
            <Upload className="mx-auto text-[#7AB2B2] mb-3" size={44} />
            <p className="text-[#666666] font-bold">لا توجد فواتير مرفوعة بعد</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="uploaded-invoices-list">
            {rows.map((r) => (
              <div key={r.id} className="bg-white border border-[#7AB2B2] rounded-xl p-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`uploaded-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-[#333333]">
                    {r.ocr_data?.invoice_number ? `فاتورة رقم ${r.ocr_data.invoice_number}` : "فاتورة مرفوعة"}
                    {isAdmin && <span className="text-xs text-[#666666] mr-2">— {r.merchant_name}</span>}
                  </div>
                  <div className="text-xs text-[#666666]">{r.uploaded_at?.slice(0, 16).replace("T", " ")}</div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    <span className={`text-[11px] font-extrabold px-2 py-0.5 rounded-full ${OCR_COLOR[r.ocr_status]}`}>{OCR_LABEL[r.ocr_status]}</span>
                    {r.ocr_status === "done" && r.review_status && REVIEW_PILL[r.review_status] && (
                      <span className={`text-[11px] font-extrabold px-2 py-0.5 rounded-full ${REVIEW_PILL[r.review_status][1]}`} data-testid={`review-pill-${r.id}`}>{REVIEW_PILL[r.review_status][0]}</span>
                    )}
                    {r.ocr_status === "done" && !r.ocr_review && <span className="text-[11px] font-extrabold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700" data-testid={`legacy-pill-${r.id}`}>قراءة سابقة</span>}
                    {r.mime_type === "application/pdf" && <span className="text-[11px] font-extrabold px-2 py-0.5 rounded-full bg-[#EBF4F6] text-[#09637E]">PDF</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => downloadOriginal(r)} data-testid={`download-uploaded-${r.id}`} title="تحميل الملف الأصلي"
                    className="h-9 px-3 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white text-xs font-bold flex items-center gap-1">
                    <Download size={14} /> تحميل
                  </button>
                  <button onClick={() => openView(r)} data-testid={`view-uploaded-${r.id}`}
                    className="h-9 px-3 rounded-lg border-2 border-[#09637E] text-[#09637E] text-xs font-bold flex items-center gap-1 hover:bg-[#EBF4F6]">
                    <Eye size={14} /> عرض
                  </button>
                  <button onClick={() => del(r)} data-testid={`delete-uploaded-${r.id}`}
                    className="h-9 w-9 rounded-lg border-2 border-red-400 text-red-500 flex items-center justify-center hover:bg-red-50">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

      {view && (
        <UploadedInvoiceReview view={view} readOnly={isAdmin} onClose={() => setView(null)}
          onChanged={(v) => { setView(v.ocr_status === "pending" ? null : v); load(); if (v.ocr_status === "pending") setTimeout(load, 15000); }} />
      )}
    </div>
  );
};

export default function Invoices() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isEmployee = user?.role === "employee";  // same-store read + historical upload; no export / branding / manual controls
  const [tab, setTab] = useState("pdf");
  return (
    <div className="space-y-5" data-testid="invoices-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333] flex items-center gap-2">
          <FileText className="text-[#09637E]" size={32} /> الفواتير
        </h1>
        <p className="text-[#666666] mt-1">{isEmployee ? "فواتير متجرك الصادرة تلقائياً من المبيعات + الفواتير المرفوعة (صورة أو PDF)" : "فواتير صادرة تلقائياً من المبيعات + بيانات البيع (Excel) + فواتير مرفوعة (صورة أو PDF)"}</p>
      </div>
      <div className="flex gap-2">
        <button onClick={() => setTab("pdf")} data-testid="tab-pdf-invoices"
          className={`flex-1 h-12 rounded-lg font-bold ${tab === "pdf" ? "bg-[#09637E] text-white" : "bg-[#EBF4F6] text-[#09637E]"}`}>
          {isAdmin ? "فواتير صادرة من الموقع" : <>فواتير المبيعات<span className="hidden md:inline"> (أونلاين + فيزيائي)</span></>}
        </button>
        <button onClick={() => setTab("uploaded")} data-testid="tab-uploaded-invoices"
          className={`flex-1 h-12 rounded-lg font-bold ${tab === "uploaded" ? "bg-[#09637E] text-white" : "bg-[#EBF4F6] text-[#09637E]"}`}>
          صور الفواتير المرفوعة
        </button>
        {!isAdmin && !isEmployee && (
          <button onClick={() => setTab("identity")} data-testid="tab-invoice-identity"
            className={`flex-1 h-12 rounded-lg font-bold ${tab === "identity" ? "bg-[#09637E] text-white" : "bg-[#EBF4F6] text-[#09637E]"}`}>
            هوية الفاتورة
          </button>
        )}
      </div>
      {tab === "pdf" ? <PdfInvoicesList isAdmin={isAdmin} isEmployee={isEmployee} /> : tab === "identity" && !isEmployee ? <InvoiceIdentity /> : <UploadedInvoices isAdmin={isAdmin} isEmployee={isEmployee} />}
    </div>
  );
}
